/**
 * URL validation utilities for secure redirect handling
 * Prevents open redirect attacks by only allowing relative paths
 */

/**
 * Validates a destination URL to ensure it's safe for redirection
 * Only allows relative paths starting with '/' and prevents external domains
 *
 * @param destination - The URL to validate
 * @returns The validated URL or null if invalid
 */
export const validateDestinationUrl = (
	destination: string | null | undefined
): string | null => {
	if (!destination || typeof destination !== 'string') {
		return null
	}

	// Remove any leading/trailing whitespace
	const trimmed = destination.trim()

	if (!trimmed) {
		return null
	}

	// Must start with '/' (relative path)
	if (!trimmed.startsWith('/')) {
		return null
	}

	// Prevent protocol-relative URLs (//example.com)
	if (trimmed.startsWith('//')) {
		return null
	}

	// Prevent javascript:, data:, or other dangerous protocols
	if (trimmed.includes(':')) {
		return null
	}

	// Basic path validation - must contain only safe characters
	const validPathPattern = /^\/[a-zA-Z0-9\-._~!$&'()*+,;=:@/?#[\]%]*$/
	if (!validPathPattern.test(trimmed)) {
		return null
	}

	return trimmed
}

/**
 * Gets a safe destination URL with fallback to default path
 *
 * @param destination - The destination URL to validate
 * @param fallback - The fallback path if destination is invalid (default: '/')
 * @returns A safe destination URL
 */
export const getSafeDestination = (
	destination: string | null | undefined,
	fallback = '/'
): string => {
	const validDestination = validateDestinationUrl(destination)
	return validDestination || fallback
}

/**
 * Creates a login URL with destination parameter
 *
 * @param destination - The destination URL to redirect to after login
 * @returns Login URL with destination parameter
 */
export const createLoginUrl = (destination?: string): string => {
	const validDestination = validateDestinationUrl(destination)
	if (validDestination) {
		const params = new URLSearchParams({ destination: validDestination })
		return `/login?${params.toString()}`
	}
	return '/login'
}
